import { Link } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { MapPin, Compass, Star, Camera, Search, TreePine, Building, PenLine, Award, Loader2 } from "lucide-react";
import { useLang } from "@/lib/lang";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { CATEGORY_COLORS, CATEGORY_ICONS } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MISSION_TYPES = [
  { id: "photo", icon: Camera, fr: "Prendre une photo du lieu", en: "Take a photo of the place" },
  { id: "observation", icon: Search, fr: "Rechercher un élément particulier", en: "Search for a specific element" },
  { id: "historical", icon: PenLine, fr: "Trouver une information historique", en: "Find a historical fact" },
  { id: "environment", icon: TreePine, fr: "Observer un élément de l'environnement", en: "Observe an environmental feature" },
  { id: "note", icon: Building, fr: "Ajouter une observation personnelle", en: "Add a personal observation" },
];

export default function NearbyGame() {
  const { lang } = useLang();
  const [loc, setLoc] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const T = lang === "fr" ? FR : EN;

  const askLocation = () => {
    setError(null);
    if (!navigator.geolocation) {
      setError(T.noGeo);
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setLoc({ lat: latitude, lng: longitude });
        try {
          const r = await axios.get(`${API}/games/nearest`, { params: { lat: latitude, lng: longitude, limit: 5 } });
          setList(r.data.top);
        } catch (e) {
          setError(T.fetchError);
        } finally {
          setLoading(false);
        }
      },
      (e) => { setError(T.geoDenied); setLoading(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const completeMission = async (missionType) => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/games/missions/complete`, {
        location_id: selected.id,
        mission_type: missionType,
        note: note.trim(),
      }, { withCredentials: true });
      toast.success(T.missionDone);
      setSelected(null);
      setNote("");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 401) toast.error(T.loginRequired);
      else toast.error(typeof detail === "string" ? detail : T.missionError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="nearby-game-page">
      <NavBar />

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-10">
        <Link to="/games" className="text-sm text-zinc-500 hover:text-[#8b6f3d]">← {T.back}</Link>
        <div className="mb-4 mt-4 flex items-center gap-3 text-[#8b6f3d]">
          <div className="h-px w-12 bg-[#8b6f3d]/40" />
          <span className="font-mono-data text-[11px] uppercase tracking-[0.3em]">{T.tag}</span>
        </div>
        <h1 className="font-serif-mystery text-4xl md:text-5xl mb-3">🗺️ {T.title}</h1>
        <p className="text-zinc-600 mb-8">{T.intro}</p>

        {list.length === 0 && (
          <div className="border border-zinc-200 rounded-2xl p-8 text-center bg-zinc-50">
            <Compass size={48} className="mx-auto text-[#8b6f3d] mb-4" />
            <p className="text-zinc-700 mb-6">{T.askLocationPrompt}</p>
            <Button data-testid="ask-location-btn" onClick={askLocation} disabled={loading}
              className="bg-[#8b6f3d] hover:bg-[#a1854b] text-white rounded-full h-12 px-6">
              {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> {T.searching}</> : <>{T.enableLocation}</>}
            </Button>
            {error && <p className="mt-4 text-sm text-red-600" data-testid="nearby-error">{error}</p>}
          </div>
        )}

        {list.length > 0 && !selected && (
          <div className="space-y-4" data-testid="nearby-list">
            {list.map((x, i) => (
              <div key={x.id} data-testid={`nearby-card-${x.id}`}
                className="border border-zinc-200 rounded-2xl p-5 hover:border-[#8b6f3d] hover:shadow-lg transition-all bg-white">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center text-2xl shrink-0"
                    style={{ background: `${CATEGORY_COLORS[x.category] || "#8b6f3d"}22` }}>
                    {CATEGORY_ICONS[x.category] || "✨"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-mono-data text-[10px] uppercase tracking-widest text-white px-2 py-0.5 rounded-full"
                        style={{ background: CATEGORY_COLORS[x.category] || "#8b6f3d" }}>
                        {x.category}
                      </span>
                      <span className="text-xs text-zinc-500 flex items-center gap-1"><MapPin size={11} /> {x.country}</span>
                      <span className="text-xs font-medium text-[#8b6f3d] flex items-center gap-1">
                        <Compass size={11} /> {x.distance_km} km
                      </span>
                    </div>
                    <h3 className="font-serif-mystery text-xl mb-2">{x.name?.[lang]}</h3>
                    <p className="text-sm text-zinc-600 line-clamp-2 mb-3">{x.description?.[lang]}</p>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1 text-xs text-amber-600">
                        <Star size={12} fill="currentColor" /> {x.confidence || 0}/10
                      </div>
                      <Button data-testid={`nearby-adventure-${x.id}`} onClick={() => setSelected(x)}
                        className="ml-auto bg-[#8b6f3d] hover:bg-[#a1854b] text-white rounded-full h-9 px-4 text-sm">
                        🧭 {T.adventure}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {selected && (
          <div className="border-2 border-[#8b6f3d] rounded-2xl p-6 bg-gradient-to-br from-amber-50/50 to-white" data-testid="mission-panel">
            <h2 className="font-serif-mystery text-2xl mb-2">🧭 {T.mission}</h2>
            <p className="text-zinc-700 italic mb-4">« {T.missionQuote} »</p>
            <div className="mb-4 p-4 rounded-xl bg-white border border-zinc-200">
              <h3 className="font-medium mb-1">{selected.name?.[lang]}</h3>
              <p className="text-sm text-zinc-600">{selected.description?.[lang]}</p>
            </div>
            <p className="text-xs text-amber-700 mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
              ⚠️ {T.safety}
            </p>

            <h3 className="font-serif-mystery text-lg mb-3">{T.chooseMission}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
              {MISSION_TYPES.map(m => (
                <button key={m.id} data-testid={`mission-btn-${m.id}`}
                  onClick={() => completeMission(m.id)}
                  disabled={submitting}
                  className="flex items-center gap-3 p-3 rounded-xl border border-zinc-200 hover:border-[#8b6f3d] hover:bg-amber-50/30 transition-colors text-left">
                  <m.icon size={18} className="text-[#8b6f3d] shrink-0" />
                  <span className="text-sm">{m[lang]}</span>
                </button>
              ))}
            </div>

            <label className="block text-xs font-mono-data uppercase tracking-widest text-zinc-500 mb-1">{T.optionalNote}</label>
            <textarea data-testid="mission-note" value={note} onChange={e => setNote(e.target.value)} rows={2}
              className="w-full border border-zinc-300 rounded-lg p-2 text-sm focus:border-[#8b6f3d] focus:outline-none" />

            <div className="mt-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800">
              ⭐ +50 {T.pointsReward}
            </div>

            <Button variant="outline" onClick={() => setSelected(null)} data-testid="mission-cancel"
              className="mt-4 rounded-full">
              {T.cancel}
            </Button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

const FR = {
  back: "Retour aux jeux",
  tag: "Jeu 1 — Exploration",
  title: "Les 5 mystères les plus proches de toi",
  intro: "Trouve les vrais mystères de la carte proches de ta position, pars les explorer sur le terrain et gagne des points.",
  askLocationPrompt: "Autorise ta géolocalisation pour trouver les mystères les plus proches. Ta position n'est utilisée que pour ce jeu.",
  enableLocation: "🧭 Activer ma géolocalisation",
  searching: "Recherche…",
  noGeo: "Ton navigateur ne supporte pas la géolocalisation.",
  geoDenied: "Géolocalisation refusée. Autorise-la dans tes paramètres pour jouer.",
  fetchError: "Erreur de chargement des mystères.",
  adventure: "Partir à l'aventure",
  mission: "Mission",
  missionQuote: "Pars à l'aventure et découvre ce qui se cache derrière ce mystère.",
  safety: "Ne pénètre jamais dans une propriété privée, une zone interdite ou un endroit dangereux. Respecte toujours les lois locales.",
  chooseMission: "Choisis ta mission sur place",
  optionalNote: "Note personnelle (optionnel)",
  pointsReward: "points par mission complétée",
  cancel: "Annuler",
  missionDone: "🎉 Mission complétée ! +50 points",
  missionError: "Erreur lors de l'envoi.",
  loginRequired: "Connecte-toi avec Google pour valider tes missions.",
};
const EN = {
  back: "Back to games",
  tag: "Game 1 — Exploration",
  title: "5 mysteries closest to you",
  intro: "Find real map mysteries near your position, head out to explore them and earn points.",
  askLocationPrompt: "Allow geolocation to find nearby mysteries. Your position is only used for this game.",
  enableLocation: "🧭 Enable my geolocation",
  searching: "Searching…",
  noGeo: "Your browser does not support geolocation.",
  geoDenied: "Geolocation denied. Allow it in your settings to play.",
  fetchError: "Failed to load mysteries.",
  adventure: "Start the adventure",
  mission: "Mission",
  missionQuote: "Head out and discover what's hidden behind this mystery.",
  safety: "Never enter private property, restricted zones or dangerous places. Always follow local laws.",
  chooseMission: "Choose your on-site mission",
  optionalNote: "Personal note (optional)",
  pointsReward: "points per completed mission",
  cancel: "Cancel",
  missionDone: "🎉 Mission completed! +50 points",
  missionError: "Failed to submit.",
  loginRequired: "Log in with Google to validate missions.",
};
